'use client';

import { useCallback, useMemo, useRef } from 'react';
import type { FormConfig, FormInput, FormSubmitPayload } from '@jlg-website/shared';
import { submitLead, type SubmitResult } from '@/components/pages/form/lead/submit-lead';
import {
  collectQuestionAnswers,
  emitFormSubmit,
  emitLeadSubmitted,
  emitQuestionsFilledOut,
  emitStepProgress,
} from '@/components/pages/form/lead/marketing';
import {
  searchAddress,
  validatePhone,
  validatePostalCode,
} from '@/components/pages/form/lead/field-validators';
import useGoogleConversion from '@/hooks/useGoogleConversion';
import { useMarketing } from '@/providers/marketing/MarketingContext';
import { Plugins, randomString } from '@jlg-website/shared';

/**
 * Shared lead-form wiring for the public form (`pages/form/client.tsx`) and every embedded one
 * (`pages/form/embedded/client.tsx` — the mass-tort hero, the footer): the web-layer field
 * validators, the marketing / analytics events, and the single-step submit path. Every surface must
 * fire the exact same events, so this lives once here instead of being copied per client — each
 * client keeps only what differs (its `autoNext` value, initial answers, the embedded redirect
 * hand-off).
 */
export function useLeadForm(schema: FormInput, config?: FormConfig) {
  // Steps already reported, so each `form_progress_*` event fires at most once.
  const emittedSteps = useRef<Set<number>>(new Set());
  // Identifies this mounted form in the `formStep` dedup id. One per hook instance, so the two
  // forms a page can carry (the mass-tort hero and the footer) never share it — both report step 1
  // on mount and each has to stay its own event.
  const formSessionId = useRef('');
  const marketingConversion = useGoogleConversion();
  // The page's campaign attribution, resolved on the server — see `MarketingProvider`. Every
  // helper below is handed it explicitly so none of them has to reach for `window.JLG`.
  const marketing = useMarketing();

  // Inject the web-layer field validators (server actions) the renderer's address, zip-code and
  // phone fields call. Kept on the client so the action references reach the fields without crossing
  // the RSC data boundary as plain props.
  const configWithValidators = useMemo<FormConfig>(
    () => ({ ...config, validators: { validatePhone, validatePostalCode, searchAddress } }),
    [config]
  );

  // Emits `form_submit`, then — for a single-step form that never triggers `onReachLastStep` —
  // fires `emitQuestionsFilledOut` with its answered questions (including any interleaved into the
  // contact block) *before* the lead is submitted. Persisting the lead comes next, followed by the
  // post-submit marketing events with the stored answers and contact.
  const handleSubmit = useCallback(
    // Returns the whole `SubmitResult`, not just the status the renderer reads: the lead id and
    // contact it carries are what the callback scheduler needs afterwards.
    async (payload: FormSubmitPayload, plugins: Plugins): Promise<SubmitResult> => {
      emitFormSubmit();

      if (schema.form_steps.length === 1) {
        const answers = collectQuestionAnswers(payload);
        if (Object.keys(answers).length > 0) {
          emitQuestionsFilledOut({ conversion: marketingConversion, payload, marketing });
        }
      }

      const result = await submitLead({ schema, payload, plugins, marketing });
      emitLeadSubmitted({
        conversion: marketingConversion,
        leadId: result.lead_id,
        answers: result.answers,
        contact: result.contact,
        isQualified: payload.is_qualified,
        marketing,
      });
      return result;
    },
    [schema, marketingConversion, marketing]
  );

  // `randomString` reads `window.crypto` and the form clients are server-rendered, so the id is
  // minted on the first event — which only ever fires in the browser — never during render.
  const handleStepNext = useCallback(
    (payload: FormSubmitPayload): void => {
      if (!formSessionId.current) formSessionId.current = randomString(16);
      emitStepProgress({
        emitted: emittedSteps.current,
        formSessionId: formSessionId.current,
        payload,
        marketing,
      });
    },
    [marketing]
  );

  const handleReachLastStep = useCallback(
    (payload: FormSubmitPayload): void =>
      emitQuestionsFilledOut({ conversion: marketingConversion, payload, marketing }),
    [marketingConversion, marketing]
  );

  return { config: configWithValidators, handleSubmit, handleStepNext, handleReachLastStep };
}
